// RFC-0944 REST client for user- and project-scoped rules.
//
// All requests route through `apiFetch` (api/client.ts), which resolves the
// configured API base URL, attaches `credentials: 'include'` (session
// affinity + auth cookie) AND the Supabase `Authorization` bearer, and applies
// the transient-retry policy. This replaces the earlier hand-rolled relative
// `fetch('/user/rules')` (API_ROOT = '') that resolved against the console
// origin (404) and never sent the auth header (latent 401).
//
// We deliberately use `apiFetch` (raw Response) rather than `apiRequest`
// because `RulesEditor` narrows failures on `err instanceof RulesRequestError
// && err.status === 409`. `apiRequest` throws its own `ApiRequestError` and
// skips schema validation. So on a non-ok response we read the body and throw
// the existing `RulesRequestError{status, body}` unchanged; on ok we validate
// through `parseRulesResponse` (schema boundary, no `as T` cast per CLAUDE.md
// #3) so a drifted payload fails at the API boundary instead of reaching the
// editor as trusted-shape data.

import { apiFetch, type ApiFetchOptions } from './client'
import {
  EMPTY_RULES_DOCUMENT,
  parseRulesConflictResponse,
  parseRulesDocumentV1,
  parseRulesHistoryEntry,
  parseRulesResponse,
  type ApiValue,
  type RulesDocumentV1,
  type RulesConflictDetail,
  type RulesConflictResponse,
  type RulesHistoryEntry,
  type RulesPutBody,
  type RulesResponse,
} from '../generated/rulesDocument'

// Typed error carrier so callers narrow via `err instanceof RulesRequestError`
// (see RulesEditor.tsx handleSave 409 branch). Body is typed against the
// generated `ApiValue` union so downstream consumers can narrow through
// isApiRecord without reintroducing `unknown`.
export class RulesRequestError extends Error {
  readonly status: number
  readonly body: ApiValue | null
  constructor(status: number, body: ApiValue | null) {
    super(`rules request failed: ${status}`)
    this.name = 'RulesRequestError'
    this.status = status
    this.body = body
  }
}

async function _fetch<T>(
  path: string,
  parse: (raw: ApiValue | null) => T,
  init?: ApiFetchOptions,
): Promise<T> {
  const resp = await apiFetch(path, init)
  if (!resp.ok) {
    const body: ApiValue | null = await resp
      .json()
      .catch(() => ({ error: `http_${resp.status}` }))
    throw new RulesRequestError(resp.status, body)
  }
  // Validate at the schema boundary — no `as T` cast per CLAUDE.md #3.
  const raw: ApiValue | null = await resp.json()
  return parse(raw)
}

export async function getUserRules(opts?: {
  history?: boolean
}): Promise<RulesResponse> {
  const params = new URLSearchParams()
  if (opts?.history) params.set('history', 'true')
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return _fetch(`/user/rules${suffix}`, parseRulesResponse)
}

export async function putUserRules(body: RulesPutBody): Promise<RulesResponse> {
  // PUTs are non-idempotent w.r.t. `expected_version`: `apiFetch` retries all
  // 5xx by default, and a committed-but-lost PUT response would retry with the
  // now-stale `expected_version` and surface a misleading 409. Disable retry.
  return _fetch(`/user/rules`, parseRulesResponse, {
    method: 'PUT',
    body: JSON.stringify(body),
    retryTransient: false,
  })
}

export async function getProjectRules(
  projectId: string,
  opts?: { history?: boolean }
): Promise<RulesResponse> {
  const params = new URLSearchParams()
  if (opts?.history) params.set('history', 'true')
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return _fetch(
    `/projects/${encodeURIComponent(projectId)}/rules${suffix}`,
    parseRulesResponse,
  )
}

export async function putProjectRules(
  projectId: string,
  body: RulesPutBody,
): Promise<RulesResponse> {
  return _fetch(
    `/projects/${encodeURIComponent(projectId)}/rules`,
    parseRulesResponse,
    {
      // See putUserRules — disable transient retry for the non-idempotent PUT.
      method: 'PUT',
      body: JSON.stringify(body),
      retryTransient: false,
    },
  )
}

export function emptyRulesDocument(): RulesDocumentV1 {
  return {
    ...EMPTY_RULES_DOCUMENT,
    structured: { ...EMPTY_RULES_DOCUMENT.structured },
  }
}

export {
  parseRulesConflictResponse,
  parseRulesDocumentV1,
  parseRulesHistoryEntry,
  parseRulesResponse,
}

export type {
  RulesConflictDetail,
  RulesConflictResponse,
  RulesDocumentV1,
  RulesHistoryEntry,
  RulesPutBody,
  RulesResponse,
}
